import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from '@chakra-ui/react';
import { Globe, ChevronDown } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import {
  COURSE_TRANSLATION_LANGUAGES,
  ENGLISH_LANGUAGE_CODE,
  getLanguage,
} from '@duke-hq/shared-constants/courseTranslationLanguages';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

// A visual match for the student navbar switcher, so an author previews what a
// learner picks. Presentational: the parent supplies which languages are ready.
export function PreviewLanguagePicker({
  value,
  onChange,
  readyByLanguage,
}: {
  value: string;
  onChange: (language: string) => void;
  // Languages absent from the map (or false) read as "not available here yet".
  readyByLanguage?: ReadonlyMap<string, boolean>;
}) {
  const selectedLanguage = value === ENGLISH_LANGUAGE_CODE ? undefined : getLanguage(value);
  const triggerLabel = selectedLanguage?.endonym ?? 'English';

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        colorScheme="gray"
        leftIcon={<Globe size={iconSize.sm} />}
        rightIcon={<ChevronDown size={iconSize.sm} />}
        data-testid={testIds.languageTrigger}
      >
        <Text
          as="span"
          lang={selectedLanguage?.code}
          dir={selectedLanguage?.rtl ? 'rtl' : 'ltr'}
          sx={{ unicodeBidi: 'isolate' }}
        >
          {triggerLabel}
        </Text>
      </MenuButton>
      <MenuList minW={pxToRem(240)} maxW={pxToRem(320)}>
        <MenuOptionGroup value={value} type="radio">
          <MenuItemOption
            value={ENGLISH_LANGUAGE_CODE}
            onClick={() => onChange(ENGLISH_LANGUAGE_CODE)}
            fontSize="sm"
            fontWeight="medium"
            data-testid={testIds.languageOption}
          >
            <Trans>English</Trans>
          </MenuItemOption>
          {COURSE_TRANSLATION_LANGUAGES.map((language) => {
            const isSelected = language.code === value;
            // Never disable the chosen language, even when it isn't ready on this
            // page: a checked-but-disabled row reads as a contradiction.
            const isUnavailable = !isSelected && readyByLanguage?.get(language.code) !== true;

            return (
              <MenuItemOption
                key={language.code}
                value={language.code}
                onClick={isUnavailable ? undefined : () => onChange(language.code)}
                isDisabled={isUnavailable}
                aria-disabled={isUnavailable}
                data-testid={testIds.languageOption}
              >
                <Flex direction="column" gap={0.5}>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={isUnavailable ? 'fg.muted' : 'fg.default'}
                    lang={language.code}
                    dir={language.rtl ? 'rtl' : 'ltr'}
                    textAlign="left"
                    sx={{ unicodeBidi: 'isolate' }}
                  >
                    {language.endonym}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    {language.englishName}
                  </Text>
                  {isUnavailable ? (
                    <Text fontSize="xs" color="fg.muted" data-testid={testIds.languageOptionHint}>
                      <Trans>Not available here yet</Trans>
                    </Text>
                  ) : null}
                </Flex>
              </MenuItemOption>
            );
          })}
        </MenuOptionGroup>
        <MenuDivider />
        {/* Aligns the note's left edge with the option labels, which sit past
            MenuItemOption's radio-icon gutter. */}
        <Text
          ps={8}
          pe={3}
          py={1}
          fontSize="xs"
          color="fg.muted"
          data-testid={testIds.languageFooterNote}
        >
          <Trans>Shows the published translation a learner sees.</Trans>
        </Text>
      </MenuList>
    </Menu>
  );
}
